import { useState, useCallback, useMemo } from 'react';
import { ServiceProvider } from '../../application/services/ServiceProvider';

const MENSAJE_POR_DEFECTO =
  'El día de hoy no es hábil para realizar pagos. Por favor intente en un día hábil.';

/**
 * Hook personalizado para validar disponibilidad de día hábil antes de un pago.
 * Centraliza la lógica usada tanto en el flujo de facturas como en el de recordatorios.
 */
export const useValidacionDiaHabil = () => {
  const [validando, setValidando] = useState<boolean>(false);
  const [mensaje, setMensaje] = useState<string>('');
  const [mostrarModal, setMostrarModal] = useState<boolean>(false);

  // Obtener el servicio a través del proveedor (memoizado para evitar recreaciones)
  const servicio = useMemo(() => {
    return ServiceProvider.getDisponibilidadHorariaService();
  }, []);

  /**
   * Valida si hoy es día hábil. Devuelve true si se puede continuar al pago,
   * false si el día no es hábil (en cuyo caso se muestra el modal de aviso).
   * Si el servicio falla, no se bloquea el flujo (devuelve true).
   */
  const validarDiaHabil = useCallback(async (): Promise<boolean> => {
    // El usuario ya vio la advertencia y la aceptó: proceder sin re-validar
    if (mensaje) {
      return true;
    }

    setValidando(true);

    try {
      const hoy = new Date();
      const fechaHoy = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
      const disponibilidad = await servicio.validar(fechaHoy);

      if (disponibilidad.esNoHabil) {
        setMensaje(disponibilidad.mensaje || MENSAJE_POR_DEFECTO);
        setMostrarModal(true);
        setValidando(false);
        return false;
      }
    } catch {
      // Si falla el servicio, no bloqueamos el flujo de pago
    }

    setValidando(false);
    return true;
  }, [mensaje, servicio]);

  /**
   * Reconoce el aviso: cierra el modal y limpia el mensaje.
   */
  const aceptarAviso = useCallback(() => {
    setMostrarModal(false);
    setMensaje('');
  }, []);

  return {
    validando,
    mensaje,
    mostrarModal,
    validarDiaHabil,
    aceptarAviso
  };
};
